import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Printer, 
  Trash2, 
  Plus, 
  Minus, 
  Search,
  Tag,
  MapPin,
  Barcode,
  Layers,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import JsBarcode from "jsbarcode";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const StockLabelPrinting = () => {
  const [pluInput, setPluInput] = useState("");
  const [searching, setSearching] = useState(false);
  const [labelQueue, setLabelQueue] = useState([]);
  const [companyInfo, setCompanyInfo] = useState({ shortName: "BBH", logo: "", offsetX: 0, offsetY: 0 });
  const [shelves, setShelves] = useState([]);
  const [selectedShelf, setSelectedShelf] = useState("");
  const [loadingShelf, setLoadingShelf] = useState(false);
  const pluInputRef = useRef(null);

  useEffect(() => {
    fetchCompanySettings();
    fetchShelves();
    if (pluInputRef.current) {
      pluInputRef.current.focus();
    }
  }, []);

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/company`);
      setCompanyInfo({
        shortName: response.data.short_name || "BBH",
        logo: response.data.logo_url || "",
        offsetX: Number.isFinite(response.data.sticker_offset_x_mm) ? Number(response.data.sticker_offset_x_mm) : 0,
        offsetY: Number.isFinite(response.data.sticker_offset_y_mm) ? Number(response.data.sticker_offset_y_mm) : 0,
      });
    } catch (error) {
      console.error("Error fetching company settings:", error);
    }
  };

  const fetchShelves = async () => {
    try {
      const response = await axios.get(`${API}/products/shelf-bins`);
      setShelves(response.data || []);
    } catch (error) {
      console.error("Error fetching shelves:", error);
    }
  };

  // Extract the numeric bin from a "SHELF/BIN" location string (e.g.
  // "J1-0/0006" → 6) so we can sort labels smallest → biggest bin. Products
  // with no bin fall to the end.
  const binNumberOf = (item) => {
    const raw = item.location?.split('/')[1] ?? item.bin_number;
    const n = parseInt(String(raw ?? '').replace(/\D/g, ''), 10);
    return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
  };

  const loadShelfProducts = async () => {
    if (!selectedShelf) {
      toast.error("Please select a shelf first");
      return;
    }
    setLoadingShelf(true);
    try {
      const response = await axios.get(`${API}/products/shelf/${encodeURIComponent(selectedShelf)}/products`);
      const products = (response.data?.products || []).slice().sort(
        (a, b) => binNumberOf(a) - binNumberOf(b)
      );
      if (products.length === 0) {
        toast.error(`No products found on shelf ${selectedShelf}`);
        return;
      }
      let added = 0;
      for (const product of products) {
        if (product.selling_price_incl_vat) {
          setLabelQueue(prev => {
            const existing = prev.find(item => item.id === product.id);
            if (existing) return prev;
            return [...prev, { ...product, quantity: 1 }];
          });
          added++;
        }
      }
      toast.success(`Added ${added} products from shelf ${selectedShelf}`);
    } catch (error) {
      console.error("Error loading shelf products:", error);
      toast.error("Failed to load shelf products");
    } finally {
      setLoadingShelf(false);
    }
  };

  const handlePluSearch = async (e) => {
    if (e.key === "Enter" && pluInput.trim()) {
      setSearching(true);
      
      try {
        const response = await axios.get(`${API}/products/search`, {
          params: { q: pluInput.trim() }
        });
        
        if (response.data && response.data.length > 0) {
          // Find exact match by PLU or barcode
          let found = response.data.find(
            p => p.plu_code?.toLowerCase() === pluInput.toLowerCase() ||
                 p.barcode?.toLowerCase() === pluInput.toLowerCase()
          );
          
          // If no exact match, take first result
          if (!found) {
            found = response.data[0];
          }
          
          if (found) {
            addToQueue(found);
          }
        } else {
          toast.error(`Product not found: ${pluInput}`);
        }
      } catch (error) {
        console.error("Search error:", error);
        toast.error("Error searching for product");
      } finally {
        setSearching(false);
        setPluInput("");
        pluInputRef.current?.focus();
      }
    }
  };

  const addToQueue = (product) => {
    setLabelQueue(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    toast.success(`Added: ${product.name}`);
  };

  const updateQuantity = (productId, delta) => {
    setLabelQueue(prev => 
      prev.map(item => {
        if (item.id === productId) {
          const newQty = Math.max(1, item.quantity + delta);
          return { ...item, quantity: newQty };
        }
        return item;
      })
    );
  };

  const removeFromQueue = (productId) => {
    setLabelQueue(prev => prev.filter(item => item.id !== productId));
  };

  const clearQueue = () => {
    setLabelQueue([]);
  };

  // Always render with CODE128 so the digits print as ONE continuous centred
  // string instead of EAN-13's gapped "2 23819 83157 7" layout — easier for
  // the cashier to read on the small sticker. Scanners decode either format
  // to the same value.
  const generateBarcodeDataUrl = (code) => {
    if (!code) return null;
    try {
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, code, {
        format: "CODE128",
        width: 3,
        height: 80,
        displayValue: true,
        fontSize: 22,
        margin: 5,
        textMargin: 3,
        font: "Arial",
        fontOptions: "bold",
        background: "#ffffff",
        lineColor: "#000000"
      });
      return canvas.toDataURL('image/png');
    } catch (e) {
      console.error("Barcode generation error:", e);
      return null;
    }
  };

  const printLabels = (includeLocation = false) => {
    if (labelQueue.length === 0) {
      toast.error("No items in print queue");
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Pop-up blocked. Please allow pop-ups for printing.");
      return;
    }

    // Generate labels HTML - matching EasyAs layout.
    // Sort by bin number (smallest → biggest) at print time so hand-added
    // items also come out in shelf order.
    const queueToPrint = labelQueue.slice().sort(
      (a, b) => binNumberOf(a) - binNumberOf(b)
    );
    let labelsHtml = '';
    queueToPrint.forEach(item => {
      // Shelf talkers (includeLocation) have no room for a barcode
      const barcodeDataUrl = includeLocation ? null : generateBarcodeDataUrl(item.barcode || item.plu_code);
      const shelfPart = item.location?.split('/')[0] || item.shelf_number || '-';
      const binPart = item.location?.split('/')[1] || item.bin_number || '-';
      
      for (let i = 0; i < item.quantity; i++) {
        labelsHtml += `
          <div class="label">
            <div class="row1">
              <div class="logo">${companyInfo.shortName || 'BBH'}</div>
              <div class="price">R${item.selling_price_incl_vat?.toFixed(2) || '0.00'}</div>
            </div>
            <div class="row2">
              <span class="plu">PLU: ${item.plu_code || ''}</span>
              ${includeLocation ? `<span class="location"><span class="loc-shelf">S:${shelfPart}</span><span class="loc-bin">B:${binPart}</span></span>` : ''}
            </div>
            ${barcodeDataUrl ? `<img class="barcode" src="${barcodeDataUrl}" alt="barcode" />` : ''}
            ${includeLocation && item.barcode ? `<div class="bc-num">${item.barcode}</div>` : ''}
            <div class="name${includeLocation ? ' name-talker' : ''}">${item.name || ''}</div>
          </div>
        `;
      }
    });

    // Safe printable area = 35mm × 27mm (2.5mm horiz × 1.5mm vert dead margin).
    // Bumped from 1mm/0.5mm — with only 1mm of slack, even a small
    // calibration offset would push the "P" in "PLU" (or the bin number
    // on the right) past the printer's physical edge and get clipped.
    // 2.5mm gives ±2mm of headroom so calibration actually keeps the
    // content on the sticker.
    const padTop = 1.5;
    const padRight = 2.5;
    const padBottom = 1.5;
    const padLeft = 2.5;
    const shiftX = companyInfo.offsetX || 0;
    const shiftY = companyInfo.offsetY || 0;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${includeLocation ? 'Shelf Talkers' : 'Stock Labels'}</title>
          <style>
            @page {
              size: 40mm 30mm;
              margin: 0;
              /* margin-left removed: invalid in @page, caused 'Print Failed' on Chromium 130+ */
            }
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0;
            }
            .label {
              width: 40mm;
              height: 30mm;
              padding: ${padTop}mm ${padRight}mm ${padBottom}mm ${padLeft}mm;
              transform: translate(${shiftX}mm, ${shiftY}mm);
              transform-origin: top left;
              overflow: hidden;
              page-break-after: always;
              display: flex;
              flex-direction: column;
              border: 1px solid #ccc;
            }
            .label:last-child {
              page-break-after: auto;
            }
            .row1 {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 0;
            }
            .logo {
              font-size: 9pt;
              font-weight: 900;
              background: #000;
              color: #fff;
              padding: 0.3mm 1.5mm;
              letter-spacing: 0.3mm;
            }
            .price {
              font-size: 13pt;
              font-weight: 900;
              color: #000;
              white-space: nowrap;
            }
            .row2 {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 0;
              font-size: 8pt;
              font-weight: bold;
              gap: 1mm;
            }
            .plu {
              font-weight: bold;
              flex-shrink: 0;
            }
            .location {
              display: flex;
              flex-direction: column;
              align-items: flex-end;
              font-weight: bold;
              font-size: 7pt;
              line-height: 1.05;
              text-align: right;
              white-space: nowrap;
            }
            .loc-shelf, .loc-bin { display: block; }
            .barcode {
              /* Narrower barcode leaves more vertical room for the product
                 name below. Height also trimmed slightly. */
              width: 30mm;
              height: auto;
              max-height: 9mm;
              object-fit: contain;
              margin: 0.3mm auto 0;
            }
            .name {
              font-size: 8pt;
              text-align: center;
              font-weight: bold;
              line-height: 1.05;
              text-transform: uppercase;
              margin-top: auto;
              overflow: hidden;
              max-height: 9mm;
              display: -webkit-box;
              -webkit-line-clamp: 3;
              -webkit-box-orient: vertical;
            }
            .name-talker { font-size: 9pt; line-height: 1.1; max-height: 12.5mm; }
            .bc-num { font-size: 7pt; font-family: monospace; text-align: center; letter-spacing: 0.3mm; margin-top: 0.3mm; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            @media print {
              body {
                margin: 0;
                padding: 0;
              }
              .label {
                border: none;
                margin: 0;
              }
            }
          </style>
        </head>
        <body>
          ${labelsHtml}
          <script>
            (function() {
              function doPrint() {
                try { window.focus(); window.print(); } catch (e) {}
                setTimeout(function() { window.close(); }, 300);
              }
              var imgs = Array.prototype.slice.call(document.images || []);
              if (imgs.length === 0) { setTimeout(doPrint, 50); return; }
              var pending = imgs.length;
              var done = false;
              function checkDone() {
                pending -= 1;
                if (pending <= 0 && !done) { done = true; setTimeout(doPrint, 50); }
              }
              imgs.forEach(function(img) {
                if (img.complete && img.naturalWidth > 0) checkDone();
                else { img.addEventListener('load', checkDone); img.addEventListener('error', checkDone); }
              });
              setTimeout(function() { if (!done) { done = true; doPrint(); } }, 3000);
            })();
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  const totalLabels = labelQueue.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="flex-1 p-6 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Tag className="h-6 w-6" />
              Stock Label Printing
            </h1>
            <p className="text-gray-600">Print stock labels and shelf talkers (40 × 30 mm)</p>
          </div>
        </div>

        {/* Scanner Input */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <Label className="text-lg font-semibold">Scan PLU / Barcode</Label>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                ref={pluInputRef}
                value={pluInput}
                onChange={(e) => setPluInput(e.target.value)}
                onKeyDown={handlePluSearch}
                data-testid="label-plu-input"
                placeholder={searching ? "Searching..." : "Scan or type PLU/Barcode and press Enter..."}
                className="h-14 text-xl pl-10"
                autoFocus
                disabled={searching}
              />
            </div>
          </CardContent>
        </Card>

        {/* Print by Shelf */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <Label className="text-lg font-semibold flex items-center gap-2">
              <Layers className="h-5 w-5" />
              Print by Shelf
            </Label>
            <p className="text-sm text-gray-500 mb-3">Load all products on a shelf to the print queue</p>
            <div className="flex gap-3">
              <select
                value={selectedShelf}
                onChange={(e) => setSelectedShelf(e.target.value)}
                className="flex-1 h-12 text-lg border rounded-md px-3 bg-white"
                data-testid="shelf-selector"
              >
                <option value="">Select a shelf...</option>
                {shelves.map(shelf => (
                  <option key={shelf} value={shelf}>{shelf}</option>
                ))}
              </select>
              <Button
                onClick={loadShelfProducts}
                disabled={!selectedShelf || loadingShelf}
                className="h-12 px-6 text-lg bg-green-600 hover:bg-green-700"
                data-testid="load-shelf-button"
              >
                {loadingShelf ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-5 w-5 mr-2" />
                )}
                Load Shelf
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Label Queue */}
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Barcode className="h-5 w-5" />
              Print Queue ({totalLabels} labels)
            </CardTitle>
            {labelQueue.length > 0 && (
              <Button variant="outline" size="sm" onClick={clearQueue}>
                <Trash2 className="h-4 w-4 mr-2" />
                Clear All
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {labelQueue.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Tag className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No items in print queue</p>
                <p className="text-sm">Scan products to add them to the queue</p>
              </div>
            ) : (
              <div className="space-y-3">
                {labelQueue.map(item => (
                  <div 
                    key={item.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="font-medium">{item.name}</div>
                      <div className="text-sm text-gray-500">
                        PLU: {item.plu_code} | R{item.selling_price_incl_vat?.toFixed(2)}
                        {item.location && ` | Loc: ${item.location}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => updateQuantity(item.id, -1)}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-10 text-center font-bold text-lg">
                        {item.quantity}
                      </span>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => updateQuantity(item.id, 1)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFromQueue(item.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Print Buttons */}
        {labelQueue.length > 0 && (
          <div className="flex gap-4">
            <Button
              onClick={() => printLabels(false)}
              data-testid="print-stock-labels-btn"
              className="flex-1 h-14 text-lg bg-blue-600 hover:bg-blue-700"
            >
              <Printer className="h-5 w-5 mr-2" />
              Print Stock Labels
            </Button>
            <Button
              onClick={() => printLabels(true)}
              data-testid="print-shelf-talkers-btn"
              className="flex-1 h-14 text-lg bg-purple-600 hover:bg-purple-700"
            >
              <MapPin className="h-5 w-5 mr-2" />
              Print Shelf Talkers
            </Button>
          </div>
        )}

        {/* Custom Text Labels */}
        <CustomLabelSection companyInfo={companyInfo} />
      </div>
    </div>
  );
};

const CustomLabelSection = ({ companyInfo = { shortName: "BBH", offsetX: 0, offsetY: 0 } }) => {
  const [customLabels, setCustomLabels] = useState([]);
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [line3, setLine3] = useState("");
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState(1);
  const [fontSize, setFontSize] = useState("medium");

  const addCustomLabel = () => {
    if (!line1.trim() && !line2.trim() && !line3.trim()) {
      toast.error("Enter at least one line of text");
      return;
    }
    setCustomLabels(prev => [...prev, {
      id: Date.now(),
      line1: line1.trim(),
      line2: line2.trim(),
      line3: line3.trim(),
      price: price.trim(),
      quantity: qty,
      fontSize,
    }]);
    setLine1("");
    setLine2("");
    setLine3("");
    setPrice("");
    setQty(1);
  };

  const removeCustomLabel = (id) => {
    setCustomLabels(prev => prev.filter(l => l.id !== id));
  };

  const updateQty = (id, delta) => {
    setCustomLabels(prev => prev.map(l => l.id === id ? { ...l, quantity: Math.max(1, l.quantity + delta) } : l));
  };

  const printCustomLabels = () => {
    if (customLabels.length === 0) {
      toast.error("No custom labels to print");
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Pop-up blocked. Please allow pop-ups for printing.");
      return;
    }

    const fontSizeMap = { small: "10pt", medium: "13pt", large: "16pt" };

    let labelsHtml = '';
    customLabels.forEach(label => {
      const fs = fontSizeMap[label.fontSize] || "13pt";
      for (let i = 0; i < label.quantity; i++) {
        labelsHtml += `
          <div class="label">
            <div class="row1">
              <div class="logo">${companyInfo.shortName || 'BBH'}</div>
              ${label.price ? `<div class="price">R${parseFloat(label.price).toFixed(2)}</div>` : ''}
            </div>
            <div class="text-area" style="font-size:${fs};">
              ${label.line1 ? `<div class="line">${label.line1}</div>` : ''}
              ${label.line2 ? `<div class="line">${label.line2}</div>` : ''}
              ${label.line3 ? `<div class="line">${label.line3}</div>` : ''}
            </div>
          </div>
        `;
      }
    });

    // Safe printable area = 35mm × 27mm — see main printLabels() for the rationale.
    const padTop = 1.5;
    const padRight = 2.5;
    const padBottom = 1.5;
    const padLeft = 2.5;
    const shiftX = companyInfo.offsetX || 0;
    const shiftY = companyInfo.offsetY || 0;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Custom Labels</title>
          <style>
            @page {
              size: 40mm 30mm;
              margin: 0;
              /* margin-left removed: invalid in @page, caused 'Print Failed' on Chromium 130+ */
            }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
            .label {
              width: 40mm;
              height: 30mm;
              padding: ${padTop}mm ${padRight}mm ${padBottom}mm ${padLeft}mm;
              transform: translate(${shiftX}mm, ${shiftY}mm);
              transform-origin: top left;
              page-break-after: always;
              display: flex;
              flex-direction: column;
              border: 1px solid #ccc;
            }
            .label:last-child { page-break-after: auto; }
            .row1 {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 0.5mm;
            }
            .logo {
              font-size: 10pt;
              font-weight: 900;
              background: #000;
              color: #fff;
              padding: 0.3mm 1.5mm;
              letter-spacing: 0.3mm;
            }
            .price {
              font-size: 14pt;
              font-weight: 900;
              color: #000;
              white-space: nowrap;
            }
            .text-area {
              flex: 1;
              display: flex;
              flex-direction: column;
              justify-content: center;
              padding: 0 1mm;
              overflow: hidden;
            }
            .line {
              font-weight: 700;
              color: #000;
              line-height: 1.3;
              text-align: center;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            @media print {
              @page {
                size: 40mm 30mm;
                margin: 0;
                /* margin-left removed: invalid in @page, caused 'Print Failed' on Chromium 130+ */
              }
              .label { border: none; margin: 0; }
            }
          </style>
        </head>
        <body>
          ${labelsHtml}
          <script>
            (function() {
              function doPrint() {
                try { window.focus(); window.print(); } catch (e) {}
                setTimeout(function() { window.close(); }, 300);
              }
              var imgs = Array.prototype.slice.call(document.images || []);
              if (imgs.length === 0) { setTimeout(doPrint, 50); return; }
              var pending = imgs.length;
              var done = false;
              function checkDone() {
                pending -= 1;
                if (pending <= 0 && !done) { done = true; setTimeout(doPrint, 50); }
              }
              imgs.forEach(function(img) {
                if (img.complete && img.naturalWidth > 0) checkDone();
                else { img.addEventListener('load', checkDone); img.addEventListener('error', checkDone); }
              });
              setTimeout(function() { if (!done) { done = true; doPrint(); } }, 3000);
            })();
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  const totalCustom = customLabels.reduce((sum, l) => sum + l.quantity, 0);

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Tag className="h-5 w-5 text-green-600" />
          Custom Text Labels
        </CardTitle>
        <p className="text-sm text-gray-500">Enter your own text to print on labels (50x40mm)</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Line 1 (main text)</Label>
              <Input
                value={line1}
                onChange={(e) => setLine1(e.target.value)}
                placeholder="e.g. SPECIAL OFFER"
                className="font-bold"
                data-testid="custom-label-line1"
              />
            </div>
            <div>
              <Label className="text-xs">Line 2</Label>
              <Input
                value={line2}
                onChange={(e) => setLine2(e.target.value)}
                placeholder="e.g. Buy 2 Get 1 Free"
                data-testid="custom-label-line2"
              />
            </div>
            <div>
              <Label className="text-xs">Line 3</Label>
              <Input
                value={line3}
                onChange={(e) => setLine3(e.target.value)}
                placeholder="e.g. Valid until 30 April"
                data-testid="custom-label-line3"
              />
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Price (optional)</Label>
              <Input
                type="number"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="e.g. 49.99"
                data-testid="custom-label-price"
              />
            </div>
            <div>
              <Label className="text-xs">Quantity</Label>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setQty(Math.max(1, qty - 1))}><Minus className="h-4 w-4" /></Button>
                <Input
                  type="number"
                  min="1"
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 text-center"
                  data-testid="custom-label-qty"
                />
                <Button variant="outline" size="sm" onClick={() => setQty(qty + 1)}><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Text Size</Label>
              <div className="flex gap-2 mt-1">
                {["small", "medium", "large"].map(s => (
                  <Button
                    key={s}
                    variant={fontSize === s ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFontSize(s)}
                    className={fontSize === s ? "bg-green-600" : ""}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
            <Button onClick={addCustomLabel} className="w-full bg-green-600 hover:bg-green-700" data-testid="add-custom-label-btn">
              <Plus className="h-4 w-4 mr-2" /> Add to Print Queue
            </Button>
          </div>
        </div>

        {/* Custom Label Queue */}
        {customLabels.length > 0 && (
          <>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2 font-medium">Text</th>
                    <th className="text-right p-2 font-medium">Price</th>
                    <th className="text-center p-2 font-medium">Size</th>
                    <th className="text-center p-2 font-medium">Qty</th>
                    <th className="text-center p-2 font-medium w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {customLabels.map(label => (
                    <tr key={label.id} className="border-t">
                      <td className="p-2">
                        <div className="font-semibold">{label.line1}</div>
                        {label.line2 && <div className="text-xs text-gray-500">{label.line2}</div>}
                        {label.line3 && <div className="text-xs text-gray-400">{label.line3}</div>}
                      </td>
                      <td className="p-2 text-right font-mono">{label.price ? `R${parseFloat(label.price).toFixed(2)}` : '-'}</td>
                      <td className="p-2 text-center text-xs capitalize">{label.fontSize}</td>
                      <td className="p-2">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => updateQty(label.id, -1)}>
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center font-bold">{label.quantity}</span>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => updateQty(label.id, 1)}>
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                      <td className="p-2 text-center">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => removeCustomLabel(label.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Button
              onClick={printCustomLabels}
              className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
              data-testid="print-custom-labels-btn"
            >
              <Printer className="h-5 w-5 mr-2" />
              Print {totalCustom} Custom Label{totalCustom !== 1 ? 's' : ''}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default StockLabelPrinting;
